import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  input,
  model,
  signal,
  untracked,
  viewChildren,
} from '@angular/core';

import { NgpTabButton, NgpTabList, NgpTabPanel, NgpTabset } from 'ng-primitives/tabs';
import { injectDisposables } from 'ng-primitives/utils';

import { I18nPipe } from '@core/i18n';

import { BadgeComponent } from '@shared/badge';
import { IconComponent } from '@shared/icon';
import { twMerge } from '@shared/tw-merge';

import { TabPanelDirective } from './tab-group.directive';
import type { SlidingRect, TabOption, TabOrientation, TabSide } from './tab-group.types';
import {
  slidingBarVariants,
  tabBadgeVariants,
  tabButtonVariants,
  tabListVariants,
  tabPanelVariants,
} from './tab-group.variants';

@Component({
  selector: 'ks-tab-group',
  imports: [
    NgpTabset,
    NgpTabList,
    NgpTabButton,
    NgpTabPanel,
    NgTemplateOutlet,
    IconComponent,
    BadgeComponent,
    I18nPipe,
  ],
  host: {
    '[class]': 'hostClass()',
  },
  templateUrl: './tab-group.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabGroupComponent {
  readonly options = input.required<TabOption[]>();
  readonly orientation = input<TabOrientation>('horizontal');
  readonly side = input<TabSide>('bottom');
  readonly extraClass = input<string>('', { alias: 'class' });

  readonly value = model<string | undefined>(undefined);

  private readonly itemRefs = viewChildren<ElementRef<HTMLButtonElement>>('item');

  protected readonly panels = contentChildren(TabPanelDirective);

  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly disposables = injectDisposables();

  private cancelRectUpdate: (() => void) | null = null;

  protected readonly rect = signal<SlidingRect | null>(null);
  protected readonly rectAnimated = signal(false);

  protected readonly tabBadgeClass = tabBadgeVariants;
  protected readonly tabBadgeOnIconClass = twMerge(tabBadgeVariants, 'absolute -top-1.75 -right-3');
  protected readonly tabPanelClass = tabPanelVariants;

  protected readonly hostClass = computed(() => twMerge('w-full', this.extraClass()));
  protected readonly tabsetClass = computed(() =>
    twMerge('w-full', this.orientation() === 'vertical' ? 'flex h-full' : ''),
  );
  protected readonly tabListClass = computed(() =>
    tabListVariants({ orientation: this.orientation(), side: this.side() }),
  );
  protected readonly tabButtonClass = computed(() =>
    tabButtonVariants({ orientation: this.orientation() }),
  );
  protected readonly slidingBarClass = computed(() =>
    twMerge(
      slidingBarVariants({ orientation: this.orientation(), side: this.side() }),
      !this.rectAnimated() && 'transition-none',
    ),
  );

  protected readonly panelsBeforeList = computed(
    () => this.orientation() === 'vertical' && this.side() === 'left',
  );

  constructor() {
    effect(() => {
      const options = this.options();
      const value = this.value();
      const valid = options.some((o) => o.value === value && !o.disabled);

      if (!valid) {
        const firstEnabled = options.find((o) => !o.disabled);

        this.value.set(firstEnabled?.value);
      }
    });

    effect(() => {
      const value = this.value();
      const refs = this.itemRefs();

      if (!refs.length) return;

      untracked(() => this.updateRect(value, refs));
    });

    const observer = new ResizeObserver(() =>
      untracked(() => this.updateRect(this.value(), this.itemRefs())),
    );

    observer.observe(this.hostRef.nativeElement as Element);
    inject(DestroyRef).onDestroy(() => observer.disconnect());
  }

  protected onValueChange(value: string | undefined): void {
    if (value === undefined) return;

    this.value.set(value);
  }

  private updateRect(
    value: string | undefined,
    refs: readonly ElementRef<HTMLButtonElement>[],
  ): void {
    this.cancelRectUpdate?.();
    this.cancelRectUpdate = null;

    if (value === undefined) {
      this.rect.set(null);

      return;
    }

    const idx = this.options().findIndex((o) => o.value === value);

    if (idx === -1 || refs[idx] === undefined) {
      this.rect.set(null);

      return;
    }

    this.cancelRectUpdate = this.disposables.requestAnimationFrame(() => {
      const itemEl = refs[idx]?.nativeElement;

      if (!itemEl) return;

      this.rect.set({
        top: itemEl.offsetTop,
        left: itemEl.offsetLeft,
        width: itemEl.offsetWidth,
        height: itemEl.offsetHeight,
      });

      if (this.rectAnimated()) return;

      this.disposables.requestAnimationFrame(() => this.rectAnimated.set(true));
    });
  }
}
